import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import { IProblemExplanationModalData } from '@datatype/iyagigo/type';
import Scroll from '@component/common/scroll';
import { v4 as uuid4 } from 'uuid';
import AudioBlock from '@component/common/block/audio-block';
import Gap from '@component/common/gap';
import { MODAL_SCROLL_HEIGHT, LARGE_MODAL_WIDTH } from '@constant/size';

interface IProps {
  data: IProblemExplanationModalData['data'];
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${LARGE_MODAL_WIDTH};
`;

const TITLE_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  box: { margin: `0px 0px ${size.MARGIN_1} 0px` },
} as const;

const ExplanationContainer = styled.div`
  //white-space: pre-wrap;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  background-color: ${color.BROWN_4};
  padding: ${size.MARGIN_1};
  gap: ${size.MARGIN_1};
`;

const LABEL_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  box: { alignX: 'left' },
} as const;

const DESCRIPTION_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left' },
} as const;

const ExplanationItemContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-flow: column wrap;
  gap: 5px;
`;

const EXPLANATION_GAP = '15px';

const ProblemExplanationModal: React.FC<IProps> = ({ data }) => {
  const {
    passage,
    passageAudioLink,
    passageTranslation,
    questionA,
    questionAAudioLink,
    questionATranslation,
    explanationA,
    vocabulary,
    grammar,
    hanja,
  } = data;

  const Vocabularies = vocabulary.map((item) => {
    return <TextBlock key={uuid4()} text={item} design={DESCRIPTION_DESIGN} />;
  });

  const Hanjas = (hanja || []).map((item) => {
    return <TextBlock key={uuid4()} text={item} design={DESCRIPTION_DESIGN} />;
  });

  const Grammars = grammar.map((item) => {
    return <TextBlock key={uuid4()} text={item} design={DESCRIPTION_DESIGN} />;
  });

  return (
    <Wrapper>
      <TextBlock text='문제풀이' design={TITLE_TEXT_DESIGN} />
      <Scroll direction='y' height={MODAL_SCROLL_HEIGHT}>
        <ExplanationContainer>
          {passageAudioLink && (
            <>
              <ExplanationItemContainer>
                <TextBlock text={'지문 듣기'} design={LABEL_DESIGN} />
                <AudioBlock
                  source={passageAudioLink}
                  timing={{ begin: 1000, end: 1000 }}
                  design={{ box: { alignX: 'left', width: '100%' } }}
                />
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          )}
          {passageAudioLink && (
            <>
              <ExplanationItemContainer>
                <TextBlock text={'지문'} design={LABEL_DESIGN} />
                <TextBlock text={passage} design={DESCRIPTION_DESIGN} />
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          )}
          {passage && (
            <>
              <ExplanationItemContainer>
                <TextBlock text={'지문 해석'} design={LABEL_DESIGN} />
                <TextBlock text={passageTranslation} design={DESCRIPTION_DESIGN} />
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          )}
          {questionAAudioLink && (
            <>
              <ExplanationItemContainer>
                <TextBlock text={'질문 듣기'} design={LABEL_DESIGN} />
                <AudioBlock
                  source={questionAAudioLink}
                  timing={{ begin: 1000, end: 1000 }}
                  design={{ box: { alignX: 'left', width: '100%' } }}
                />
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          )}
          {questionAAudioLink && (
            <>
              <ExplanationItemContainer>
                <TextBlock text={'질문'} design={LABEL_DESIGN} />
                <TextBlock text={questionA} design={DESCRIPTION_DESIGN} />
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          )}
          {questionAAudioLink && (
            <>
              <ExplanationItemContainer>
                <TextBlock text={'질문 해석'} design={LABEL_DESIGN} />
                <TextBlock text={questionATranslation} design={DESCRIPTION_DESIGN} />
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          )}

          {
            <>
              <ExplanationItemContainer>
                <TextBlock text={'문제 풀이'} design={LABEL_DESIGN} />
                {explanationA && <TextBlock text={'- ' + explanationA} design={DESCRIPTION_DESIGN} />}
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          }
          {vocabulary && vocabulary.length > 0 && (
            <>
              <ExplanationItemContainer>
                <TextBlock text={'단어'} design={LABEL_DESIGN} />
                <DescriptionContainer>{Vocabularies}</DescriptionContainer>
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          )}
          {hanja && hanja.length > 0 && (
            <>
              <ExplanationItemContainer>
                <TextBlock text={'한자'} design={LABEL_DESIGN} />
                <DescriptionContainer>{Hanjas}</DescriptionContainer>
              </ExplanationItemContainer>
              <Gap height={EXPLANATION_GAP} />
            </>
          )}
          {grammar && grammar.length > 0 && (
            <ExplanationItemContainer>
              <TextBlock text={'문법'} design={LABEL_DESIGN} />
              <DescriptionContainer>{Grammars}</DescriptionContainer>
            </ExplanationItemContainer>
          )}
        </ExplanationContainer>
      </Scroll>
    </Wrapper>
  );
};

export default ProblemExplanationModal;
